import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import Date from '~/modules/common/components/Date';
import DateRange from '~/modules/common/components/DateRange';
import { PERIOD_SCALE_ENUM } from '~/modules/common/charts/timeline/periodScale';
import { useMeContext } from '~/modules/me';
import {
  getUserPreferenceWeekStart,
  getUserPreferenceWeekEnd
} from '~/modules/common/dates/dayOfWeek';
import { getQuarterFromMonth } from '~/modules/common/dates/quarter';

const TodayPeriodValue = ({ scale }) => {
  const me = useMeContext();

  const { start, end } = useMemo(() => {
    const today = DateTime.local();

    if (scale === PERIOD_SCALE_ENUM.WEEKS) {
      const weekStart = getUserPreferenceWeekStart(me, today);
      const weekEnd = getUserPreferenceWeekEnd(me, today);

      return {
        start: weekStart,
        end: weekEnd
      };
    }

    return {
      start: today,
      end: today
    };
  }, [scale, me]);

  if (scale === PERIOD_SCALE_ENUM.DAYS) {
    return <Date data-qe-id="DateField" value={start} />;
  }

  if (scale === PERIOD_SCALE_ENUM.WEEKS) {
    return <DateRange start={start} end={end} />;
  }

  if (scale === PERIOD_SCALE_ENUM.QUARTERS) {
    return (
      <>
        <FormattedMessage id="chart.quarterAbbreviated" />
        {getQuarterFromMonth(start.month)}&nbsp;
        {end.toLocaleString({ year: 'numeric' })}
      </>
    );
  }

  if (scale === PERIOD_SCALE_ENUM.YEARS) {
    return start.toLocaleString({ year: 'numeric' });
  }

  return start.toLocaleString({ month: 'long', year: 'numeric' });
};

TodayPeriodValue.propTypes = {
  scale: PropTypes.oneOf([
    PERIOD_SCALE_ENUM.DAYS,
    PERIOD_SCALE_ENUM.WEEKS,
    PERIOD_SCALE_ENUM.MONTHS,
    PERIOD_SCALE_ENUM.QUARTERS,
    PERIOD_SCALE_ENUM.YEARS
  ]).isRequired
};

export default TodayPeriodValue;
